import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import { API } from './config/api';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BannerDisplay from './components/BannerDisplay';
import LoadingSpinner from './components/LoadingSpinner';
import MaintenancePage from './pages/MaintenancePage';

// Pages
import HomePage from './pages/HomePage';
import AuctionsPage from './pages/AuctionsPage';
import AuctionDetailPage from './pages/AuctionDetailPage';
import ResultsPage from './pages/ResultsPage';
import ResultDetailPage from './pages/ResultDetailPage';
import AttualitaPage from './pages/AttualitaPage';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import CompletedAuctionsPage from './pages/CompletedAuctionsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import MyBidsPage from './pages/MyBidsPage';
import AdminDashboard from './components/AdminDashboard';
import WinnerOverlay from './components/WinnerOverlay';
import {
  HowToSellPage,
  PrivacyPage,
  CookiePage,
  CustomerServicePage,
  AboutPage,
  PartnerAuctionsPage,
  AuctionTermsPage,
  TermsPage,
  ContactPage
} from './pages/StaticPages';

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/aste" element={<AuctionsPage />} />
      <Route path="/asta/:id" element={<AuctionDetailPage />} />
      <Route path="/risultati" element={<ResultsPage />} />
      <Route path="/risultati/:dateKey" element={<ResultDetailPage />} />
      <Route path="/attualita" element={<AttualitaPage />} />
      <Route path="/notizie" element={<NewsPage />} />
      <Route path="/notizia/:id" element={<NewsDetailPage />} />
      <Route path="/come-vendere" element={<HowToSellPage />} />
      <Route path="/termini" element={<TermsPage />} />
      <Route path="/contatti" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registrati" element={<RegisterPage />} />
      <Route path="/profilo" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/mie-offerte" element={<ProtectedRoute><MyBidsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/completate" element={<CompletedAuctionsPage />} />
      <Route path="/assistenza" element={<CustomerServicePage />} />
      <Route path="/chi-siamo" element={<AboutPage />} />
      <Route path="/aste-partner" element={<PartnerAuctionsPage />} />
      <Route path="/termini-asta" element={<AuctionTermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookie" element={<CookiePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Content - checks maintenance mode
const AppContent = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);

  useEffect(() => {
    axios.get(`${API}/maintenance`)
      .then(res => setMaintenance(res.data.maintenance))
      .catch(() => setMaintenance(false))
      .finally(() => setCheckingMaintenance(false));
  }, []);

  if (checkingMaintenance || authLoading) return <LoadingSpinner />;

  // Allow login/register pages even in maintenance mode
  const isAuthPage = location.pathname === '/login' || location.pathname === '/registrati' || location.pathname === '/auth/callback' || location.hash?.includes('session_id=');

  // Allow admin pages when user is admin
  const isAdmin = user && user.role === 'admin';
  
  // Also allow if user has a session token (just logged in, user state may not be ready yet)
  const hasToken = !!localStorage.getItem('session_token');

  // Show maintenance page ONLY for non-admin, non-auth pages, and no token
  if (maintenance && !isAuthPage && !isAdmin && !hasToken) {
    return <MaintenancePage />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Banner - sopra tutto come Travera */}
      <BannerDisplay position="top" />
      <Navbar />
      <main className="flex-1">
        <AppRouter />
      </main>
      <Footer />
      <WinnerOverlay />
    </div>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
